import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

export interface Region { x: number; y: number; w: number; h: number }

interface Props {
  /** The full screenshot (a blob). */
  image: Blob;
  onConfirm: (region: Region | null) => void;
  onCancel: () => void;
}

const MIN = 0.01;
const clamp = (n: number) => Math.min(1, Math.max(0, n));

/**
 * D-323 s2: the screen dims, the user drags a rectangle on the problem area (or "כל המסך").
 * Region = fractions (0..1) of the screenshot, so it is independent of the screen size.
 */
export default function RegionSelector({ image, onConfirm, onCancel }: Props) {
  const { t } = useTranslation();
  const [url, setUrl] = useState<string | null>(null);
  const [region, setRegion] = useState<Region | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const u = URL.createObjectURL(image);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [image]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const point = useCallback((e: React.PointerEvent) => {
    const r = boxRef.current!.getBoundingClientRect();
    return { x: clamp((e.clientX - r.left) / r.width), y: clamp((e.clientY - r.top) / r.height) };
  }, []);

  const down = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    startRef.current = point(e);
    setRegion(null);
  };
  const move = (e: React.PointerEvent) => {
    const s = startRef.current;
    if (!s) return;
    const p = point(e);
    setRegion({ x: Math.min(s.x, p.x), y: Math.min(s.y, p.y), w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y) });
  };
  const up = () => {
    startRef.current = null;
    setRegion((r) => (r && (r.w < MIN || r.h < MIN) ? null : r));
  };

  return createPortal(
    <div className="fixed inset-0 z-modal flex flex-col bg-black/70" role="dialog" aria-modal="true" aria-label={t('bugFlow.selectTitle')} data-testid="bug-region-selector">
      <div className="flex flex-wrap items-center gap-3 bg-black px-4 py-3 text-white">
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold">{t('bugFlow.selectTitle')}</p>
          <p className="text-sm opacity-80">{t('bugFlow.selectHint')}</p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={() => onConfirm(null)} data-testid="bug-region-full">{t('bugFlow.fullScreen')}</button>
        <button type="button" className="btn btn-primary" disabled={!region} onClick={() => onConfirm(region)} data-testid="bug-region-continue">{t('bugFlow.continue')}</button>
        <button type="button" className="btn btn-ghost text-white" onClick={onCancel}>{t('bugFlow.cancel')}</button>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center p-3">
        {url && (
          <div
            ref={boxRef}
            className="relative max-h-full max-w-full cursor-crosshair touch-none select-none"
            style={{ lineHeight: 0 }}
            onPointerDown={down}
            onPointerMove={move}
            onPointerUp={up}
            onPointerCancel={up}
          >
            <img src={url} alt="" draggable={false} className="max-h-[calc(100vh-7rem)] max-w-full object-contain" />
            <div className="pointer-events-none absolute inset-0 bg-black/40" />
            {region && (
              <div
                className="pointer-events-none absolute border-2 border-danger-500"
                style={{
                  left: `${region.x * 100}%`, top: `${region.y * 100}%`, width: `${region.w * 100}%`, height: `${region.h * 100}%`,
                  backgroundImage: `url(${url})`,
                  backgroundSize: `${100 / Math.max(region.w, 0.0001)}% ${100 / Math.max(region.h, 0.0001)}%`,
                  backgroundPosition: `${region.w >= 1 ? 0 : (region.x / (1 - region.w)) * 100}% ${region.h >= 1 ? 0 : (region.y / (1 - region.h)) * 100}%`,
                }}
                data-testid="bug-region-rect"
              />
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** The screenshot with a red frame around the area (the full screenshot is uploaded as well). */
export async function drawMarkedScreenshot(image: Blob, region: Region | null): Promise<Blob> {
  if (!region) return image;
  const bmp = await createImageBitmap(image);
  const canvas = document.createElement('canvas');
  canvas.width = bmp.width;
  canvas.height = bmp.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return image;
  ctx.drawImage(bmp, 0, 0);
  ctx.strokeStyle = 'rgb(225, 29, 72)';
  ctx.lineWidth = Math.max(3, Math.round(bmp.width / 300));
  ctx.strokeRect(region.x * bmp.width, region.y * bmp.height, region.w * bmp.width, region.h * bmp.height);
  return await new Promise<Blob>((resolve) => canvas.toBlob((b) => resolve(b || image), 'image/png'));
}
